import { Navigate, Outlet } from "react-router-dom";
import jwt_decode from "jwt-decode";

type JWTPayload = {
  exp?: number;          // seconds since epoch (standard JWT)
  created_at?: number;   // peut être en ms (13 chiffres) ou en s (10 chiffres)
};

// ---- Helper: parse created_at proprement (ms ou s) ----
function toMsFromMaybeSecondsOrMs(v: number | undefined): number | undefined {
  if (typeof v !== "number" || !isFinite(v)) return undefined;
  // Heuristique simple: >= 10^12 => millisecondes, sinon secondes
  return v >= 1e12 ? v : v * 1000;
}

// ---- Helper: décide si le token est expiré ----
function isExpired(payload: JWTPayload): boolean {
  const nowMs = Date.now();

  // 1) Standard JWT: exp (en secondes)
  if (typeof payload.exp === "number") {
    const expMs = payload.exp * 1000;
    return expMs <= nowMs;
  }

  // 2) Fallback: created_at (ms ou s) + fenêtre de validité
  //    Ici 30 jours pour éviter les déconnexions intempestives
  const createdAtMs = toMsFromMaybeSecondsOrMs(payload.created_at);
  if (typeof createdAtMs === "number") {
    const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
    return createdAtMs + THIRTY_DAYS <= nowMs;
  }

  // 3) Pas d’info d’expiration -> on considère non expiré
  return false;
}

export const ProtectedRoutes = () => {
  const token = (typeof window !== "undefined") ? localStorage.getItem("token") : null;
  if (!token) return <Navigate to="/login" replace />;

  try {
    const payload = jwt_decode<JWTPayload>(token);

    // Si le token n’est pas expiré, on laisse passer
    if (!isExpired(payload)) {
      return <Outlet />;
    }

    // Expiré -> on nettoie et on renvoie au login
    localStorage.removeItem("token");
    return <Navigate to="/login" replace />;
  } catch (e) {
    // Token corrompu / illisible -> on nettoie et on renvoie au login
    localStorage.removeItem("token");
    return <Navigate to="/login" replace />;
  }
};